import React, { useState, useEffect, useContext, useRef } from 'react';
import { postMessage } from '../utils/common.utils';
import { useConsents } from '../hooks/useConsents';
import { useConsentActions } from '../hooks/useConsentActions';
import ConsentList from './ConsentList';
import NoConsentsFound from './NoConsentsFound';
import ActionButtons from './ActionButtons';
import FipDropdown from './FipDropdown'; // Import the new dropdown component
import EntityDropdown from './EntityDropdown';
import LeftArrow from '../assets/Left_arrow.svg';
import { UserContext } from '../context/UserContext';
import useIsMobile from '../hooks/useIsMobile';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { announceToScreenReader } from '../utils/a11y.utils';
import RevokeConfirmDialog from './RevokeConfirmDialog';

const Revoke = (props) => {
	const isMobile = useIsMobile();
	const userContext = useContext(UserContext);
	const consentIdList = userContext.consentIdList;
	const isFip = userContext.isFip;
	const fipEntity = userContext.fipEntity;
	const [isLogout, setIsLogout] = useState(false);
	const [arrowHover, setArrowHover] = useState(false);
	const [selectedFip, setSelectedFip] = useState(null);
	const [fipList, setFipList] = useState([]);
	const [filteredAccounts, setFilteredAccounts] = useState([]);
	const [entityList, setEntityList] = useState([]);
	const [selectedEntity, setSelectedEntity] = useState(null);
	const [displayedAccounts, setDisplayedAccounts] = useState([]);
	const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
	const ecid = global.ecid || null;

	const {
		allAccounts,
		selectedAccounts,
		setSelectedAccounts,
		consentNotFound,
		mobileNumber,
		AAHandle,
		logout,
		authApis,
	} = useConsents(props.updateLoader, props.history);

	const {
		revokeSpinner,
		selectUnselectAccount,
		revokeConsents,
		resumeConsents,
		viewConsentDetails,
	} = useConsentActions(authApis, props.updateLoader, props.history);

	const history = useHistory();
	const skipSelectionAnnouncement = useRef(true);

	const handleBack = () => {
		userContext.setIsFIP(false);
		history.push('/entity-selection');
	};

	useEffect(() => {
		if (props?.location?.state?.selectedAccounts) {
			setSelectedAccounts(props.location.state.selectedAccounts);
		}
	}, [
		allAccounts,
		props?.location?.state?.selectedAccounts,
		setSelectedAccounts,
	]);

	useEffect(() => {
		if (isFip && allAccounts.length > 0) {
			const fipSet = new Set();
			let tempFipList = [];

			allAccounts.forEach((account) => {
				let key = `${account.accountDetails.fiType}-${account.accountDetails.accType}-${account.accountDetails.maskedAccNumber}-${account.accountDetails.linkRefNumber}`;
				if (!fipSet.has(key)) {
					fipSet.add(key);
					tempFipList.push(account);
				}
			});

			setFipList(tempFipList);

			// Set the first FIP as selected by default
			if (tempFipList.length > 0 && !selectedFip) {
				setSelectedFip(tempFipList[0]);
			}
		}
	}, [allAccounts, isFip]);

	// Filter accounts based on selected FIP
	useEffect(() => {
		if (isFip && selectedFip && allAccounts.length > 0) {
			const filtered = allAccounts.filter(
				(account) =>
					account.accountDetails.fiType === selectedFip.accountDetails.fiType &&
					account.accountDetails.accType ===
						selectedFip.accountDetails.accType &&
					account.accountDetails.maskedAccNumber ===
						selectedFip.accountDetails.maskedAccNumber &&
					account.accountDetails.linkRefNumber ===
						selectedFip.accountDetails.linkRefNumber
			);
			setFilteredAccounts(filtered);
		} else {
			if(consentIdList.length > 0){
				const filtered = allAccounts.filter(
					(account) => consentIdList.includes(account.consentIntentId)
				);
				setFilteredAccounts(filtered);
			} else {
			setFilteredAccounts(allAccounts);
			}
		}
	}, [selectedFip, allAccounts, isFip, consentIdList]);

	// Build entity list from filtered accounts (for non-FIP mode)
	useEffect(() => {
		if (!isFip && filteredAccounts.length > 0) {
			const entityMap = new Map();
			
			filteredAccounts.forEach((account) => {
				const entityName = account.consentIntentEntityName;
				if (!entityMap.has(entityName)) {
					entityMap.set(entityName, {
						count: 0,
						entityLogoUri: account.entityLogoUri
					});
				}
				const entityData = entityMap.get(entityName);
				entityMap.set(entityName, {
					...entityData,
					count: entityData.count + 1
				});
			});

			const entities = Array.from(entityMap.entries()).map(([name, data]) => ({
				name,
				count: data.count,
				entityLogoUri: data.entityLogoUri,
			}));
			// Add "All" option at the beginning
			const allOption = {
				name: ecid || consentIdList.length > 0 ? entities[0].name :  'All' ,
				count: filteredAccounts.length,
			};

			const newEntityList = [allOption, ...entities];
			setEntityList(newEntityList);

			// Set "All" as default if not already set, or if selected entity no longer exists
			if (!selectedEntity) {
				setSelectedEntity(allOption);
			} else {
				const entityStillExists = newEntityList.some(e => e.name === selectedEntity.name);
				if (!entityStillExists) {
					setSelectedEntity(allOption);
				} else {
					// Update the count for the selected entity
					const updatedEntity = newEntityList.find(e => e.name === selectedEntity.name);
					if (updatedEntity && updatedEntity.count !== selectedEntity.count) {
						setSelectedEntity(updatedEntity);
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredAccounts, isFip]);

	// Filter displayed accounts based on selected entity
	useEffect(() => {
		if (!isFip && selectedEntity && filteredAccounts.length > 0) {
			if (selectedEntity.name === 'All') {
				setDisplayedAccounts(filteredAccounts);
			} else {
				const filtered = filteredAccounts.filter(
					(account) => account.consentIntentEntityName === selectedEntity.name
				);
				setDisplayedAccounts(filtered);
			}
		} else {
			setDisplayedAccounts(filteredAccounts);
		}
	}, [selectedEntity, filteredAccounts, isFip]);

	const handleSelectAccount = (account) => {
		selectUnselectAccount(account, selectedAccounts, setSelectedAccounts);
	};

	const handleRevoke = () => {
		setShowRevokeConfirm(true);
	};

	const handleConfirmRevoke = () => {
		revokeConsents(selectedAccounts, mobileNumber);
	};

	const handleCloseRevokeConfirm = () => {
		if (!revokeSpinner) {
			setShowRevokeConfirm(false);
		}
	};

	const handleResume = () => {
		resumeConsents(selectedAccounts, mobileNumber);
	};

	const handleViewDetails = (consentId, entityName, consent) => {
		viewConsentDetails(
			consentId,
			entityName,
			selectedAccounts,
			mobileNumber,
			consent
		);
	};

	const handleOkay = () => {
		setIsLogout(true);
		logout();
	};

	const handleSelectFip = (fip) => {
		setSelectedFip(fip);
		// Clear selected accounts when switching FIP
		setSelectedAccounts([]);
	};

	const handleSelectEntity = (entity) => {
		setSelectedEntity(entity);
		// Clear selected accounts when switching entity
		setSelectedAccounts([]);
	};

	// Handle Select All
	const handleSelectAll = () => {
		if (isAllSelected) {
			// Deselect all displayed accounts
			const remainingSelected = selectedAccounts.filter(
				selected => !displayedAccounts.some(
					displayed => displayed.consentIntentId === selected.consentIntentId
				)
			);
			setSelectedAccounts(remainingSelected);
		} else {
			// Select all displayed accounts
			const newSelections = displayedAccounts.filter(
				displayed => !selectedAccounts.some(
					selected => selected.consentIntentId === displayed.consentIntentId
				)
			);
			setSelectedAccounts([...selectedAccounts, ...newSelections]);
		}
	};

	// Check if all displayed accounts are selected
	const isAllSelected = displayedAccounts.length > 0 && displayedAccounts.every(
		displayed => selectedAccounts.some(
			selected => selected.consentIntentId === displayed.consentIntentId
		)
	);

	useEffect(() => {
		if (skipSelectionAnnouncement.current) {
			skipSelectionAnnouncement.current = false;
			return;
		}

		const count = selectedAccounts.length;
		announceToScreenReader(
			count === 0
				? 'No consents selected'
				: `${count} consent${count === 1 ? '' : 's'} selected`
		);
	}, [selectedAccounts.length]);

	return (
		<div
			style={{
				width: isMobile ? '95%' : '40%',
				margin: '0 auto',
			}}
		>
			<div className="container mt-10">
				<div className="row" style={{ paddingBottom: isMobile ? '180px' : '118px' }}>
					<div>
						{/* HEADER */}
						<div className="px-0">
							<div className="d-flex flex-row align-items-center">
								{!global.ecid && consentIdList.length === 0 && (
									<button
										type="button"
										className="card-action-btn"
										onClick={handleBack}
										onMouseEnter={() => setArrowHover(true)}
										onMouseLeave={() => setArrowHover(false)}
										aria-label="Go back"
										style={{
											display: 'inline-flex',
											borderRadius: '50%',
											background: arrowHover ? '#f0f0f0' : 'transparent',
											transition: 'background 0.2s',
											marginLeft: '-20px',
										}}
									>
										<img
											src={LeftArrow}
											alt=""
											aria-hidden="true"
											style={{ cursor: 'pointer', height: 24, width: 24 }}
										/>
									</button>
								)}
								<h1
									className="fs-14 fw-600 text-start mb-0 ms-2"
									style={{ color: '#4F4F4F' }}
								>
									MANAGE CONSENTS
									{isFip && (
										<span
											className="fs-14 fw-600 text-start mb-0 ms-1"
											style={{ color: '#4F4F4F' }}
										>
											GIVEN FOR ACCOUNTS YOU OWN
										</span>
									)}
									{!isFip && (
										<span
											className="fs-14 fw-600 text-start mb-0 ms-1"
											style={{ color: '#4F4F4F' }}
										>
											GIVEN TO APPS YOU USE
										</span>
									)}
								</h1>
							</div>
							{!consentNotFound && (
								<p
									className="fs-14 fw-500 text-start mt-3 px-2"
									style={{ color: '#6B6D6F' }}
								>
									Select to revoke or resume access on your accounts
								</p>
							)}

							{/* FIP Dropdown */}
							{!consentNotFound && isFip && fipList.length > 0 && (
								<div className="mt-3 px-2">
									<label id="fip-dropdown-label" className="form-field-label">
										Select account
									</label>
									<FipDropdown
										labelId="fip-dropdown-label"
										fipList={fipList}
										selectedFip={selectedFip}
										onSelectFip={handleSelectFip}
										customDetails={props.customDetails}
									/>
								</div>
							)}

						{/* Entity Dropdown for Apps */}
						{!consentNotFound && !isFip && entityList.length > 0 && (
							<div className="mt-3" style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 0 : '16px', alignItems: 'center' }}>
								<label id="entity-dropdown-label" className="form-field-label w-100">
									Select app
								</label>
								<EntityDropdown
									labelId="entity-dropdown-label"
									entityList={entityList}
									selectedEntity={selectedEntity}
									onSelectEntity={handleSelectEntity}
									customDetails={props.customDetails}
								/>
								<div className="d-flex align-items-center justify-content-between"
								style={{
										backgroundColor: "#F6F6F6",
										borderRadius: '8px',
										border: '2px solid #D9D9D9',
										padding: '8px 16px',
										cursor: 'pointer',
										gap: '8px',
										minHeight: '44px',
										flex: isMobile ? '1 1 100%' : '0 0 100%',
									}}
								>
									<span className="fw-600" style={{ fontSize: '14px', color: '#000' }}>Consents</span>
								<button
									type="button"
									className="card-action-btn d-flex align-items-center gap-2"
									onClick={handleSelectAll}
									aria-label={isAllSelected ? 'Deselect all consents' : 'Select all consents'}
									style={{ cursor: 'pointer' }}
								>
										<span className="fw-semibold" style={{ fontSize: '12px', color: '#6B6D6F' }}>
											Select All
										</span>
									<div
										className="d-flex align-items-center justify-content-center"
										aria-hidden="true"
										style={{
											width: '16px',
											height: '16px',
											border: isAllSelected ?  '2px solid #273C89' : '2px solid #6B6D6F',
											borderRadius: '3px',
											backgroundColor: isAllSelected ? props.customDetails?.primaryBtnColor : 'white',
										}}
									>
										{isAllSelected && (
											<svg width="12" height="10" viewBox="0 0 12 10" fill="none">
												<path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
											</svg>
										)}
									</div>
								</button>
								</div>
							</div>
						)}
						</div>

						{/* Consent List */}
						{displayedAccounts?.length > 0 && (
							<ConsentList
								consents={displayedAccounts}
								selectedAccounts={selectedAccounts}
								onSelectAccount={handleSelectAccount}
								onViewDetails={handleViewDetails}
								customDetails={props.customDetails}
								groupByEntity={!isFip && selectedEntity?.name === 'All'}
							/>
						)}

						{consentNotFound && (
							<NoConsentsFound
								customDetails={props.customDetails}
								onOkay={handleOkay}
								isLogout={isLogout}
							/>
						)}
					</div>
				</div>
			</div>

			{/* ActionButtons - same width as above */}
			{displayedAccounts?.length > 0 && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						width: isMobile ? '76%' : '32%',
						margin: '0 auto',
					}}
				>
					<ActionButtons
						onRevoke={handleRevoke}
						onResume={handleResume}
						onExit={logout}
						selectedAccounts={selectedAccounts}
						isDisable={revokeSpinner}
						customDetails={props.customDetails}
					/>
				</div>
			)}

			<RevokeConfirmDialog
				isOpen={showRevokeConfirm}
				onClose={handleCloseRevokeConfirm}
				onConfirm={handleConfirmRevoke}
				accounts={selectedAccounts}
				customDetails={props.customDetails}
				isLoading={revokeSpinner}
				groupByEntity={!isFip}
			/>
		</div>
	);
};

export default Revoke;
